import React, { useState } from "react";

import TuneIcon from "@mui/icons-material/Tune";
import { Button, IconButton, Popover } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";

import { useReportsActions } from "../../../store";
import { ReportsQueries } from "../../../store/types";

import "./GroupByMenu.styles.scss";

type GroupByMenuProps = React.PropsWithChildren<{
  children: (React.ReactElement | null) | (React.ReactElement | null)[];
  disabled?: boolean;
  icon?: React.ReactElement;
  tooltip?: string;
  renderTrigger?: (params: { onClick: React.MouseEventHandler }) => React.ReactElement;
  // When provided, the draft values are committed here instead of the global reports store.
  onOkay?: (values: Partial<ReportsQueries>) => void;
  // When provided, replaces the default global reset behavior.
  onReset?: () => void;
}>;

export const GroupByMenu: React.FC<GroupByMenuProps> = ({
  children,
  disabled,
  icon = <TuneIcon />,
  tooltip = "Table Configuration",
  renderTrigger,
  onOkay,
  onReset
}) => {
  // const [isOpen, setIsOpen] = useState<boolean>(false);
  const [values, setValues] = useState<Partial<ReportsQueries>>({});

  const { setQueriesAction } = useReportsActions();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
      handleClose();
      return;
    }

    const resetValues: typeof values = {};
    Object.entries(values).forEach(([k, value]) => {
      const key = k as keyof ReportsQueries;
      if (key === "rowBy") {
        resetValues[key] = "deal";
      } else if (key === "currency") {
        resetValues[key] = "USD";
      } else if (key === "exchangeMethod") {
        resetValues[key] = "toDate";
      } else if (key === "groupBy") {
        resetValues[key] = [["assetClass"]];
      } else if (key === "groupByGraph") {
        resetValues[key] = null;
      } else if (key === "columnType") {
        resetValues[key] = "stacked";
      } else if (key === "dealTableColumnBy") {
        resetValues[key] = "investor";
      } else if (key === "cumulative") {
        resetValues[key] = false;
      } else if (key === "mainParameter") {
        resetValues[key] = ["Adjusted NAV"];
      } else if (key === "secondaryParameter") {
        resetValues[key] = ["IRR"];
      } else if (Array.isArray(value)) {
        //@ts-ignore
        resetValues[key] = [];
      } else if (typeof value === "object") {
        //@ts-ignore
        resetValues[key] = {};
      } else {
        //@ts-ignore
        resetValues[key] = value;
      }
    });
    setValues((prev) => ({ ...prev, cumulative: false }));
    setQueriesAction(resetValues);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "group-by-popover" : undefined;

  return (
    <>
      {renderTrigger ? (
        React.createElement(renderTrigger, {
          onClick: handleClick
        })
      ) : (
        <MaterialTooltip isShow title={tooltip} placement="top">
          <IconButton
            // sx={{ opacity: "0" }}
            disabled={disabled}
            className="group-by__tune"
            id="group-by-btn"
            aria-describedby={id}
            onClick={handleClick}
          >
            {icon}
          </IconButton>
        </MaterialTooltip>
      )}
      <Popover
        sx={{ ".MuiPopover-paper": { overflow: "visible !important" } }}
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        onClose={handleClose}
      >
        <div className="group-by-menu">
          {/* <div className="head indent">Deal Measures Configuration</div> */}
          <div className="body indent">
            {Array.isArray(children)
              ? children.map((child, index) => {
                  return (
                    child &&
                    React.cloneElement(child, {
                      key: index,
                      onChangeDataFormation: (df: Partial<ReportsQueries>) => setValues({ ...values, ...df })
                    })
                  );
                })
              : children &&
                React.cloneElement(children, {
                  onChangeDataFormation: (df: Partial<ReportsQueries>) => setValues({ ...values, ...df })
                })}
          </div>
          <div className="footer indent">
            <div className="footer-box">
              <span onClick={handleReset} className="click-text">
                Reset
              </span>
              <div className="footer-box__btns">
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (onOkay) {
                      onOkay(values);
                    } else {
                      setQueriesAction(values);
                    }
                    handleClose();
                  }}
                >
                  Okay
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};
